import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import ClientFormModal from "@/components/ClientFormModal";
import SettingsModal from "@/components/SettingsModal";
import MasterLibraryModal from "@/components/MasterLibraryModal";
import ClientDashboardBody from "@/components/ClientDashboardBody";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";

export default function ClientDashboardPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";
  const [editOpen, setEditOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  const { data: client, isLoading, isError } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => api.getClient(clientId),
    enabled: !!clientId,
    retry: 1,
  });

  const updateM = useMutation({
    mutationFn: ({ id, payload }) => api.updateClient(id, payload),
    onSuccess: (c) => {
      qc.setQueryData(["client", c.id], c);
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Client updated");
      setEditOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const applyPackM = useMutation({
    mutationFn: () => api.applyPack(clientId, 12),
    onSuccess: (r) => {
      const c = r.generatedCount || 0;
      toast.success(
        c > 0
          ? `${c} compliance task${c === 1 ? "" : "s"} added from the standard pack`
          : "All standard compliances already applied"
      );
      qc.invalidateQueries({ queryKey: ["tasks", clientId] });
      qc.invalidateQueries({ queryKey: ["client", clientId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  useEffect(() => {
    if (isError) {
      toast.error("Client not found");
      // For client-role users we cannot bounce back to '/' — ClientsPage
      // immediately redirects them back to their own client, which would
      // create an infinite navigation loop (React error #185) if the
      // fetch keeps failing. Sign them out instead so they can try again.
      if (isClient) {
        setTimeout(() => logout(), 0);
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [isError, isClient, logout, navigate]);

  if (!client) {
    return (
      <>
        <AppHeader
          variant="overview"
          onOpenSettings={isAdmin ? () => setSettingsOpen(true) : undefined}
        />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
          <div className="text-sm text-[#64748B]">Loading client…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHeader
        variant="client"
        client={client}
        onBack={isClient ? undefined : () => navigate("/dashboard")}
        onEditClient={isAdmin ? () => setEditOpen(true) : undefined}
        primaryLabel="Add Task"
        primaryTestId={TID.addTaskBtn}
        onPrimary={() => setAddTaskOpen(true)}
        onOpenSettings={isAdmin ? () => setSettingsOpen(true) : undefined}
        onOpenLibrary={isAdmin ? () => setLibraryOpen(true) : undefined}
      />
      {isAdmin && (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-4">
          <button
            type="button"
            onClick={() => applyPackM.mutate()}
            disabled={applyPackM.isPending}
            data-testid={TID.applyPackBtn}
            className="inline-flex items-center gap-2 rounded-md border border-[#E2E8F0] bg-white h-8 px-3 text-xs font-medium text-[#334155] hover:bg-[#F8FAFC] disabled:opacity-50"
          >
            {applyPackM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Refresh standard compliance pack (12 months)
          </button>
        </div>
      )}

      <ClientDashboardBody
        clientId={clientId}
        addTaskOpen={addTaskOpen}
        setAddTaskOpen={setAddTaskOpen}
      />

      <ClientFormModal
        mode="edit"
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={client}
        onSubmit={(payload) => updateM.mutate({ id: client.id, payload })}
        submitting={updateM.isPending}
      />

      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
      <MasterLibraryModal
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
      />
    </>
  );
}
